import React, { useMemo, useState } from 'react';

import classnames from 'classnames';

import LiveTvIcon from '@mui/icons-material/LiveTv'; // TODO: Add Icon in Sprite

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import LinkButton from 'components/uiLibrary/LinkButton';
import Modal from 'components/uiLibrary/Modal';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import LsVodSource from 'components/LsVod/LsVodSource';

import { cueTvMediaUrlRegex } from 'constants/consts';
import { TP } from 'constants/index';
import { route } from 'constants/routes';

import usePageContext from 'utils/hooks/usePageContext';
import { getProductionWorks } from 'utils/productions';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import DateRangeAndPrefCount from '../../ProductionStubInfo/DateRangeAndPrefCount';
import WorkTitle from '../ProductionInfo/WorkTitle';
import { groupPerformances } from '../utils';

import classes from './LivestreamAndVod.module.scss';

const ProductionTitle = ({ production }) => {
  const works = useMemo(() => getProductionWorks(production?.productionWorks), [production]);
  const productionName = production?.name;
  const showCustomTitle = works?.length > 1 || (productionName && works?.[0]?.originalName !== productionName);

  return (
    <WorkTitle
      title={showCustomTitle ? productionName || ' ' : ''}
      works={works}
      styles={{ containerRoot: classes.workRoot, workTitle: classes.workTitle }}
    />
  );
};

const SourceRenderer = ({ partner, domainName, title, isLive, t }) => {
  if (!partner && !domainName && !title && !isLive) {
    return null;
  }

  return (
    <Typography size={14} className={classes.source} weight="medium" color="link" truncate>
      {partner || domainName || title} {isLive && t(`${TP}.FN_LIVE`)}
    </Typography>
  );
};

const LsVodSubscription = ({ paymentType, cost }) => {
  const { t } = useTranslation('NS_DISPLAY_V4');

  if (paymentType === 'free') {
    return (
      <Typography size={12} color="secondary" className={classnames(classes.subscriptionTag, classes.freeTag)}>
        {t(`${TP}.FN_FREE`)}
      </Typography>
    );
  }

  if ((paymentType === 'subscription' && cost) || paymentType === 'pay-per-view') {
    return (
      <Typography color="secondary" size={12} className={classnames(classes.subscriptionTag, classes.paidTag)}>
        {t(`${TP}.PAID`)}
      </Typography>
    );
  }

  return null;
};

const WatchOption = ({ performance }) => {
  const isCancelled = performance?.isCancelled;
  const { partner, url, paymentType, cost } = performance?.attributes?.[0] || {};

  const eventTime = performance?.startTime;
  const eventZone = performance?.timezone;

  return (
    <LinkButton
      variant="native"
      href={url}
      styles={{ root: classnames(classes.watchOption, { [classes.isCancelled]: isCancelled }) }}
      disabled={performance?.lsVodData?.isLive && !url?.match(cueTvMediaUrlRegex)}
      isLink
      external
    >
      <div className={classes.primaryRow}>
        <div className={classes.left}>
          <LiveTvIcon fontSize="small" className={classes.watchIcon} alt="livestream" />
          <LsVodSource
            title={url}
            partner={partner?.name}
            isLive={performance?.lsVodData?.liveToday}
            renderer={SourceRenderer}
          />
        </div>

        <div className={classes.right}>
          <LsVodSubscription paymentType={paymentType} cost={cost} />
          <SpriteIcon icon="open_in_new" size={16} className={classes.openInNewTabIcon} />
        </div>
      </div>
      <div className={classes.secondaryRow}>
        <Typography size={10} weight="medium" color="primary" className={classes.time}>
          {eventTime}
        </Typography>
        {eventZone && (
          <Typography size={10} color="secondary" className={classes.timeZone}>
            {eventZone}
          </Typography>
        )}
      </div>
    </LinkButton>
  );
};

const WatchOptions = ({ liveStreams, fullVideos, styles, limit }) => {
  const combinedWatchOptions = [...liveStreams, ...fullVideos];

  return (
    <div className={classnames(classes.watchOptions, { [styles?.watchOptionsBlock]: !!styles?.watchOptionsBlock })}>
      {combinedWatchOptions.slice(0, limit).map((performance, index) => (
        <WatchOption key={index} performance={performance} />
      ))}
    </div>
  );
};

const LivestreamAndVod = ({ production, styles, limit }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');
  const { entityType } = usePageContext();
  const [openModal, setOpenModal] = useState(false);
  const { LIVESTREAM: liveStreams, FULL_VIDEO: fullVideos } = groupPerformances(production);

  if (!liveStreams.length && !fullVideos.length) {
    return null;
  }

  if (entityType === ENTITY_TYPES.PRODUCTION) {
    return (
      <>
        <div className={classes.prodContainer}>
          <WatchOptions liveStreams={liveStreams} fullVideos={fullVideos} styles={styles} limit={limit} />
          {liveStreams.length + fullVideos.length > 2 && (
            <Typography size={14} color="link" onClick={() => setOpenModal(true)} className={classes.viewMore}>
              {t(`${TP}.FN_VIEW_MORE`)}
              <SpriteIcon icon="chevron_right" size={16} />
            </Typography>
          )}
        </div>
        <Modal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          allowMobileDrawer
          header={
            <div className={classes.modalHeader}>
              <div className={classes.modalHeader__backAction}>
                <SpriteIcon icon="arrow_back" size={16} />
                <Typography size={14} color="primary" weight="bold">
                  {t(`${TP}.FN_WATCH_OPTIONS`)}
                </Typography>
              </div>
              <div className={classes.modalHeader__titleBlock}>
                <DateRangeAndPrefCount production={production} />
                <ProductionTitle production={production} />
              </div>
              <div className={classes.modalHeader__closeAction}>
                <SpriteIcon icon="close" size={16} className={classes.closeIcon} />
              </div>
            </div>
          }
        >
          <div className={classes.modalWrapper}>
            <WatchOptions
              liveStreams={liveStreams}
              fullVideos={fullVideos}
              styles={{ watchOptionsBlock: classes.modalWatchOptionsBlock }}
            />
          </div>
        </Modal>
      </>
    );
  }

  return (
    <div className={classes.container}>
      <Typography variant="p" size={12} color="primary" weight="bold">
        {t(`${TP}.FN_WATCH_OPTIONS`)}
      </Typography>
      <HorizontalScroller outset>
        <WatchOptions liveStreams={liveStreams} fullVideos={fullVideos} />
      </HorizontalScroller>
    </div>
  );
};

export default LivestreamAndVod;
